import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { gql, useMutation } from '@apollo/client';
import {
    Table,
    Label,
    Icon,
    Header,
    Image,
} from 'semantic-ui-react';

import { FULL_STATUS_OPTIONS } from "../../../constants";
import { PATHS } from "../../../paths";
import { useIsAdmin } from "../../../hooks/isAdmin";

import { StatusUpdater } from '../../SessionUpdater';
import { PrintRoleForSessionButton } from "../../Protected/RolePrint";
import { PersonCourseAttendance } from '../../Protected/PersonCourseAttendance';

const DELETE_ATTENDANCE = gql`
mutation DeleteAttendance($attendanceId: Int) {
  deleteAttendance(attendanceId: $attendanceId) {
    status
  }
}`;

const AdminSessionView = ({ session , canEditCourse, refetchCourse }: { session: any, canEditCourse:any, refetchCourse: any }) => {
  const isAdmin = useIsAdmin();
  const [triggerRefetch, setTriggerRefetch] = useState(true);

  const [deleteAttendance] = useMutation(DELETE_ATTENDANCE);

  if (!session) {
      return null;
  }

  const handleDelete = async (id: number) => {
      await deleteAttendance({ variables: {
          attendanceId: id,
      }})

      refetchCourse();
  }

  // need a sorted copy of the attendance array
  // Check if the session startTime is in the last 3 days or if the startTime is in the next 60 minutes
  const isInRange =
    new Date(Number(session.startTime)) > new Date(Date.now() - 36 * 60 * 60 * 1000)
    && new Date(Number(session.startTime)) < new Date(Date.now() + 60 * 60 * 1000);

  const renderAttendanceRow = (a: any) => (
      <Table.Row key={a.id} style={{ backgroundColor: a.isTeacher ? '#e6f7ff' : 'transparent' }}>
          <Table.Cell width={1} >
          <Header as='h4' image>
              <Header.Content>
                <Image avatar src={a.user.photoUrl} size='tiny' style={{ width: '3em', height: '3em', objectFit: 'cover' }}/>
                {/* Name */}
                <Link to={`${PATHS.PEOPLE}/${a.user.id}`}>
                  {a.user.name} ({a.user.identifier})
                </Link>
                {/* Attendance percentage */}
                <br />
                {" (Attendance: "}
                <PersonCourseAttendance
                  personId={Number(a.user.id)}
                  triggerRefetch={triggerRefetch}
                  courseId={session.course.id} />
                {")"}

                  {isAdmin && (<>
                        <Icon color='red' name='trash alternate outline' onClick={() => handleDelete(a.id)} />
                    </>
                    )}
                </Header.Content>
            </Header>
            </Table.Cell>
            <Table.Cell width={1}>
              {((canEditCourse && isInRange) || isAdmin) ? (
                <StatusUpdater
                  refetch={() => {
                      setTriggerRefetch(!triggerRefetch)
                      refetchCourse();
                  }}
                  sessionId={session.id}
                  userId={a.user.id}
                  initStatus={a.status}
                  initDetails={a.details}
                />
                ) : (
                  <>
                    {FULL_STATUS_OPTIONS.find(s => s.value === a.status)?.text === 'Pending' ? (
                      new Date(Number(session.startTime)) > new Date(Date.now()) ? 'input available soon' : 'no data'
                    ): FULL_STATUS_OPTIONS.find(s => s.value === a.status)?.text}
                    {a.details?.length > 0 ? `: ${a.details}`: ''}
                  </>
                )
              }
            </Table.Cell>
        </Table.Row>
  );

  const teachers = session.attendance.filter((a: any) => a.isTeacher);
  const students = session.attendance.filter((a: any) => !a.isTeacher);

  return (
      <div>
          <Label size='small'>
            <Icon name='time' /> {session.durationMinutes} minutes
          </Label>
          {' '}
          <PrintRoleForSessionButton sessionId={session.id} />

          {teachers.length > 0 && (
            <div style={{ marginTop: '1em' }}>
              <Header as='h5' style={{ marginBottom: '0.25em' }}>
                <Icon name='user' /> Teachers
              </Header>
              <Table celled>
                <Table.Body>
                  {teachers.map(renderAttendanceRow)}
                </Table.Body>
              </Table>
            </div>
          )}

          <div style={{ marginTop: teachers.length > 0 ? '1.5em' : '1em' }}>
            {teachers.length > 0 && (
              <Header as='h5' style={{ marginBottom: '0.25em' }}>
                <Icon name='users' /> Students
              </Header>
            )}
            <Table celled>
              <Table.Body>
                {students.map(renderAttendanceRow)}
              </Table.Body>
            </Table>
          </div>
        </div>
    )
  }

  export { AdminSessionView }
